/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getClassNameFactory,
  FlexGrid,
  Heading,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
  Sizes,
  TextBoldness,
  Responsive,
} from '@dcl/ui';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { types, boldTypes } from '@dcl/ui/dist/elements/Heading';
import './PlaylistTiles.css';
import { PlaylistVideoPlayer } from './components/PlaylistVideoPlayer/PlaylistVideoPlayer';
import { useExtractKalturaScript } from '@dcl/ui/dist/fragments';
import { VideoTabList } from './components/VideoTabList/VideoTabList';
import { IVideoPlaylist, IVideoPlaylistDetail } from '../../types';
import { DESKTOP_WIDTH, MOBILE_WIDTH } from '../../../UltraTaxCS/constants';
import { VideoTabListMobile } from './components/VideoTabList/VideoTabListMobile';
import { VIDEO_DATA_API } from './constants';
import fetchApi from '../../../../utils/fetchApi';

export const PlaylistTiles: React.FC<IVideoPlaylist> = (
  props: IVideoPlaylist
) => {
  const getClassName = getClassNameFactory(PlaylistTiles.displayName);

  const { title, subTitle, userKeyKs, videoPlaylistDetails } = props;

  const [playListUserData, setPlayListUserData] = useState<
    IVideoPlaylistDetail[]
  >(videoPlaylistDetails);

  const [currentEntryId, setCurrentEntryId] = useState(
    videoPlaylistDetails[0].entryId
  );

  useExtractKalturaScript();

  const currentVideoHandler = (video: IVideoPlaylistDetail) => {
    setCurrentEntryId(video.entryId);
  };
  const createVideoData = (videoMetaData: any, userKeyKs: string) => {
    const baseVideoData = {
      videoTitle: '',
      videoDescription: '',
      videoViews: '',
      videoDuration: '',
      userKeyKs,
    };

    if (videoMetaData.objectType === 'KalturaMediaEntry') {
      return {
        videoData: {
          ...baseVideoData,
          videoTitle: videoMetaData.name,
          videoDescription: videoMetaData.description,
          videoViews: videoMetaData.views,
          videoDuration: videoMetaData.duration,
        },
      };
    }

    return { videoData: baseVideoData };
  };
  const initiateVideoDataApi = useCallback(
    async (entryId) => {
      const videoResponse = await fetchApi({
        baseHostUrl: VIDEO_DATA_API,
        options: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entryId: entryId,
            version: -1,
            ks: userKeyKs,
            format: 1,
          }),
        },
      });

      const videoMetaData = await videoResponse
        .text()
        .then((data) => JSON.parse(data));
      return createVideoData(videoMetaData, userKeyKs);
    },
    [userKeyKs]
  );

  useEffect(() => {
    const updateUserData = async () => {
      const updatedUserDataPromises = videoPlaylistDetails.map(
        async (playList) => {
          const { videoData } = await initiateVideoDataApi(playList.entryId);
          return { ...playList, videoData };
        }
      );
      const updatedUserData = await Promise.all(updatedUserDataPromises);
      setPlayListUserData(updatedUserData);
    };
    if (userKeyKs !== '') {
      updateUserData();
    }
  }, [initiateVideoDataApi, videoPlaylistDetails, userKeyKs]);

  return (
    <div className={getClassName()}>
      <div>
        <Heading
          type={types.Small}
          boldType={boldTypes.medium}
          data-testid="test-title"
          className={getClassName({
            descendantName: 'title',
          })}
        >
          <div title={title}>{title}</div>
        </Heading>
        <VerticalSpacing size={VerticalSpacingSizes.S}>
          <Typography
            type={Sizes.XXTRASMALL}
            tag={TypographyTags.P}
            boldness={TextBoldness.Medium}
            data-testid="test-subtitle"
            className={getClassName({
              descendantName: 'subTitle',
            })}
          >
            {subTitle}
          </Typography>
        </VerticalSpacing>
      </div>
      <br />
      <FlexGrid className={getClassName({ descendantName: 'videplayer-card' })}>
        <FlexGrid.Row>
          <FlexGrid.Col md={3} lg={3} sm={12}>
            <Responsive minWidth={DESKTOP_WIDTH}>
              <VideoTabList
                playListUserData={playListUserData}
                currentVideoHandler={currentVideoHandler}
              />
            </Responsive>
            <Responsive minWidth={0} maxWidth={MOBILE_WIDTH}>
              <VideoTabListMobile
                playListUserData={playListUserData}
                currentVideoHandler={currentVideoHandler}
              />
            </Responsive>
          </FlexGrid.Col>
          <FlexGrid.Col md={9} lg={9} sm={12}>
            {playListUserData.map((playList) => {
              if (currentEntryId === playList.entryId && playList.videoData)
                return (
                  <PlaylistVideoPlayer
                    userKeyKs={userKeyKs}
                    entryId={playList.entryId}
                    key={playList.entryId}
                    title={playList.videoData.videoTitle}
                    duration={playList.videoData.videoDuration}
                    views={playList.videoData.videoViews}
                    format={playList.videoFormat}
                  />
                );
              else return null;
            })}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

PlaylistTiles.displayName = 'PlaylistTiles';
